/**
 * Property Right Form - Schema-Based Version
 * Uses BuiltInFormRenderer for consistent behavior across all property forms
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, ArrowRightSquare } from 'lucide-react';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { UseAutosaveReturn } from '../../hooks/useAutosave';
import BuiltInFormRenderer from '../DynamicForm/BuiltInFormRenderer';
import { propertyRightFormSchema } from '../../schemas/propertyRightFormSchema';
import type { FieldSchema } from '../../types/formSchema';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

export interface PropertyRightFormProps {
  onNavigateToRooms: () => void;
  onNavigateBackToPropertyRear: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  autosave?: UseAutosaveReturn;
  onSaveImmediately?: () => Promise<string>;
  onGalleryStateChange?: (isOpen: boolean) => void;
  customFields?: FieldSchema[];
  hiddenFields?: string[];
  hiddenSections?: string[];
  sectionOrder?: string[];
  builtInFieldOrder?: string[];
}

const PropertyRightFormSchemaVersion: React.FC<PropertyRightFormProps> = ({
  onNavigateToRooms,
  onNavigateBackToPropertyRear,
  surveyData,
  surveyId,
  floorPlanId,
  onDataUpdate,
  autosave,
  onSaveImmediately,
  onGalleryStateChange,
  customFields = [],
  hiddenFields = [],
  hiddenSections = [],
  sectionOrder = [],
  builtInFieldOrder = []
}) => {
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<Record<string, any[]>>({});
  const [customFieldData, setCustomFieldData] = useState<Record<string, any>>({});
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);

  const migrationRunOnceRef = useRef(false);
  const isInternalUpdate = useRef(false);

  // Load data from surveyData
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRight] Skipping main data reload - internal update');
      return;
    }
    
    if (surveyData?.property_right) {
      const rightData = surveyData.property_right as any;
      
      const photoFields = new Set([
        'rightElevationImage',
        'roofTypeEvidenceImages',
        'rightSideImages',
        'gutteringDrainageImages',
        'wallTypeEvidenceImage',
        'wallDefectImages'
      ]);
      
      const newFormData: Record<string, any> = {};
      const newPhotos: Record<string, any[]> = {};
      
      Object.entries(rightData).forEach(([key, value]) => {
        if (photoFields.has(key)) {
          newPhotos[key] = Array.isArray(value) ? value : (value ? [value] : []);
        } else {
          newFormData[key] = value;
        }
      });
      
      setFormData(newFormData);
      setPhotos(newPhotos);
    }
  }, [surveyData?.property_right]);

  // Load custom field values
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRight] Skipping custom field reload - internal update');
      return;
    }
    
    if (surveyData?.property_right && customFields && customFields.length > 0) {
      const existingData: Record<string, any> = {};
      customFields.forEach(field => {
        const value = (surveyData.property_right as any)?.[field.id];
        if (value !== undefined) {
          existingData[field.id] = value;
        }
      });
      if (Object.keys(existingData).length > 0) {
        setCustomFieldData(existingData);
      }
    }
  }, [surveyData?.property_right, customFields]);

  // Reset internal update flag after all data loaders have run
  useEffect(() => {
    if (isInternalUpdate.current) {
      console.log('[PropertyRight] Resetting internal update flag');
      isInternalUpdate.current = false;
    }
  }, [surveyData]);

  // Photo migration
  useEffect(() => {
    if (!photos || Object.keys(photos).length === 0) return;
    if (migrationRunOnceRef.current) return;
    
    let needsMigration = false;
    Object.values(photos).forEach(photoArray => {
      if (!Array.isArray(photoArray)) return;
      photoArray.forEach(photo => {
        if ((photo.dataUrl || photo.base64String) && (!photo.url || photo.url === '')) {
          needsMigration = true;
        }
      });
    });
    
    if (!needsMigration) return;
    
    migrationRunOnceRef.current = true;
    
    Object.entries(photos).forEach(([photoType, photoArray]) => {
      if (!Array.isArray(photoArray)) return;
      
      photoArray.forEach(photo => {
        if (!photo.dataUrl && !photo.base64String) return;
        if (photo.url && photo.url !== '') return;
        
        const base64Data = photo.dataUrl || photo.base64String || '';
        queuePhotoUpload(
          photo.displayId,
          base64Data,
          'property_right',
          photoType,
          async (url: string) => {
            setPhotos(current => ({
              ...current,
              [photoType]: current[photoType].map(p => 
                p.displayId === photo.displayId 
                  ? { 
                      displayId: p.displayId, 
                      format: p.format, 
                      url, 
                      uploading: false, 
                      error: undefined,
                      // Keep dataUrl temporarily for display until CDN confirms
                      dataUrl: p.dataUrl,
                      base64String: p.base64String
                    }
                  : p
              )
            }));
            
            // Remove base64 data after a short delay to ensure CDN URL is working
            setTimeout(() => {
              setPhotos(current => ({
                ...current,
                [photoType]: current[photoType].map(p =>
                  p.displayId === photo.displayId
                    ? { displayId: p.displayId, format: p.format, url, uploading: false, error: undefined }
                    : p
                )
              }));
            }, 500);
          },
          (error: string) => {
            console.error(`Migration upload failed for ${photoType}:`, error);
          }
        );
      });
    });
  }, [photos, queuePhotoUpload]);

  const handleFormDataChange = (fieldId: string, value: any) => {
    // Check if this is a photo field
    const isPhotoField = fieldId.endsWith('_photos') || fieldId.endsWith('Image') || fieldId.endsWith('Images');
    
    // If it's a photo field, also update photos state
    if (isPhotoField) {
      setPhotos(prev => ({ ...prev, [fieldId]: value }));
    }
    
    setFormData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          const photosToSave = isPhotoField ? { ...photos, [fieldId]: value } : photos;
          onDataUpdate(prevData => ({
            ...prevData,
            property_right: {
              ...updated,
              ...photosToSave,
              ...customFieldData
            } as any
          }), 'property_right');
        }, 50);
      }
      
      return updated;
    });
  };

  const handlePhotosChange = (fieldId: string, newPhotos: any[]) => {
    const newPhotoData = newPhotos.map(photo => {
      let dataUrl = photo.dataUrl || photo.base64String;
      if (dataUrl && !dataUrl.startsWith('data:')) {
        const format = photo.format || 'jpeg';
        dataUrl = `data:image/${format};base64,${dataUrl}`;
      }
      
      return {
        displayId: `${Date.now()}_${Math.random()}`,
        dataUrl,
        format: photo.format || 'jpeg',
        uploading: true
      };
    });
    
    setPhotos(prev => {
      const updated = {
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...newPhotoData]
      };
      
      newPhotoData.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'property_right',
          fieldId,
          async (url: string) => {
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p => 
                p.displayId === photo.displayId 
                  ? { displayId: p.displayId, format: p.format, url, uploading: false }
                  : p
              )
            }));
          },
          (error: string) => {
            setPhotos(current => ({
              ...current,
              [fieldId]: (current[fieldId] || []).map(p => 
                p.displayId === photo.displayId 
                  ? { ...p, uploading: false, error }
                  : p
              )
            }));
          }
        );
      });
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate(prevData => ({
            ...prevData,
            property_right: {
              ...formData,
              ...updated,
              ...customFieldData
            } as any
          }), 'property_right');
        }, 50);
      }
      
      return updated;
    });
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldData(prev => {
      const updated = { ...prev, [fieldId]: value };
      
      if (onDataUpdate) {
        setTimeout(() => {
          isInternalUpdate.current = true;
          onDataUpdate(prevData => ({
            ...prevData,
            property_right: {
              ...formData,
              ...photos,
              ...updated
            } as any
          }), 'property_right');
        }, 50);
      }
      
      return updated;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Property Right Form Data:', { formData, photos, customFieldData });
    onNavigateToRooms();
  };

  const handleGalleryOpen = (fieldId: string) => {
    setSelectedGallery(fieldId);
    if (onGalleryStateChange) onGalleryStateChange(true);
  };

  const handleGalleryClose = () => {
    setSelectedGallery(null);
    if (onGalleryStateChange) onGalleryStateChange(false);
  };

  // Remove photo function (properly deletes from storage and saves to DB)
  const removePhoto = async (photoType: string, displayIdToRemove: string) => {
    console.log(`[PropertyRight] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    const currentPhotos = photos[photoType] || [];
    const photoToDelete = currentPhotos.find((p: any) => p.displayId === displayIdToRemove);
    
    if (photoToDelete) {
      let filePathToDelete = photoToDelete.fileName;
      
      if (!filePathToDelete && photoToDelete.url && photoToDelete.url.includes('/storage/v1/object/public/')) {
        const urlParts = photoToDelete.url.split('/storage/v1/object/public/')[1];
        if (urlParts) {
          const [bucket, ...pathParts] = urlParts.split('/');
          filePathToDelete = pathParts.join('/');
          console.log(`[PropertyRight] 🗑️ Extracted path from URL: ${filePathToDelete}`);
        }
      }
      
      if (filePathToDelete) {
        try {
          console.log(`[PropertyRight] 🗑️ Deleting from storage: ${filePathToDelete}`);
          await deletePhotoFromSupabase(filePathToDelete, 'retrofit-survey-images');
          console.log(`[PropertyRight] ✅ Deleted from storage`);
        } catch (error) {
          console.warn(`[PropertyRight] ⚠️ Failed to delete from storage:`, error);
        }
      } else {
        console.log(`[PropertyRight] ℹ️ No fileName/URL found, skipping storage deletion`);
      }
    }
    
    const filteredPhotos = currentPhotos.filter((p: any) => p.displayId !== displayIdToRemove);
    console.log(`[PropertyRight] ✅ Removed photo from state, ${filteredPhotos.length} remaining`);
    
    setPhotos(prev => ({ ...prev, [photoType]: filteredPhotos }));
    setFormData(prev => ({ ...prev, [photoType]: filteredPhotos }));
    
    if (onDataUpdate) {
      setTimeout(() => {
        isInternalUpdate.current = true;
        onDataUpdate(prevData => ({
          ...prevData,
          property_right: {
            ...formData,
            ...photos,  // Keep all other photo fields
            [photoType]: filteredPhotos,  // Override with updated field
            ...customFieldData
          } as any
        }), 'property_right');
      }, 50);
    }
  };

  const allFormData = { ...formData, ...customFieldData };

  return (
    <>
      <BuiltInFormRenderer
        schema={propertyRightFormSchema}
        formData={allFormData}
        photos={photos}
        onFormDataChange={(fieldId, value) => {
          if (customFields.some(f => f.id === fieldId)) {
            handleCustomFieldChange(fieldId, value);
          } else {
            handleFormDataChange(fieldId, value);
          }
        }}
        onPhotosChange={handlePhotosChange}
        onSubmit={handleSubmit}
        customFields={customFields}
        hiddenFields={hiddenFields}
        hiddenSections={hiddenSections}
        sectionOrder={sectionOrder}
        builtInFieldOrder={builtInFieldOrder}
        navigationButtons={{
          previous: {
            label: '← Previous',
            onClick: onNavigateBackToPropertyRear
          },
          next: {
            label: 'Next: Rooms →',
            onClick: onNavigateToRooms
          }
        }}
        onGalleryOpen={handleGalleryOpen}
        selectedGallery={selectedGallery}
        formIcon={<ArrowRightSquare size={24} className="text-green-600" />}
      />

      {selectedGallery && photos[selectedGallery] && (
        <PhotoGalleryModal
          isOpen={true}
          onClose={handleGalleryClose}
          photos={photos[selectedGallery].map((photo: any) => ({
            id: photo.displayId,
            url: photo.url || photo.dataUrl,
            localPath: photo.dataUrl
          }))}
          sectionTitle={selectedGallery}
          onDeletePhoto={(photoId) => removePhoto(selectedGallery, photoId)}
          onUpdateNote={(photoId, note) => {
            setPhotos(prev => ({
              ...prev,
              [selectedGallery]: prev[selectedGallery].map(p => 
                p.displayId === photoId ? { ...p, note } : p
              )
            }));
          }}
        />
      )}
    </>
  );
};

export default PropertyRightFormSchemaVersion;
